import { useIdle } from '@corets/use-idle';
import Spinner from '@service-victoria/component-library/components/Icons/Spinner';
import Body from '@service-victoria/component-library/components/Text/Body';
import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation } from 'react-query';
import { Redirect, useHistory } from 'react-router';
import { v4 as uuid } from 'uuid';

import BusinessDetailsSubContent from 'Components/BusinessDetailsSubContent';
import CheckinForm from 'Components/CheckinForm';
import { CheckinFormHandle } from 'Components/CheckinForm/CheckinForm';
import LinkButton from 'Components/LinkButton';
import Page from 'Components/Page';
import { useRegisteredLocations } from 'hooks';
import useModal from 'hooks/useModal';
import PrivacyModal from 'services/contentService/PrivacyModal';
import checkInService from 'services/dvrsService';
import { CheckInParams, CheckInResponse } from 'services/dvrsService/dvrsService';
import { PrimaryContactWithGuests, TrackCheckInEvent } from 'types/checkin';
import { constants } from 'utils';
import { trackEvent } from 'utils/analytics';
import { decode } from 'utils/challenge';
import { Routes } from 'utils/constants';

import { CenterAlign, ContentContainer } from './styled';

const privacyModal = new PrivacyModal();

export const CheckIn: React.FC = () => {
  const [checkinSuccess, setCheckinSuccess] = useState(false);
  const checkinForm = useRef<CheckinFormHandle>(null);

  const modal = useModal();
  const [reloadOnClear, setReloadOnClear] = useState(false);
  const [eventID, setEventID] = useState(uuid());
  const { selectedLocation, removeAllRegisteredLocations } = useRegisteredLocations();
  const history = useHistory();

  const isIdle = useIdle({
    // check every 10s
    interval: 10 * 1000,
    // user as idle after 1min
    threshold: 60 * 1000,
    // events that should reset the idle status (default: ["mousemove", "mouseup", "keyup", "focus", "resize"])
    events: ['mouseup', 'touchend', 'keyup', 'focus']
  });

  const trackCheckinEvent: TrackCheckInEvent = (label, data) => {
    trackEvent({
      label,
      action: 'KioskCheckIn',
      data: {
        ...data,
        checkinType: 'kiosk',
        locationId: selectedLocation?.locationId ?? '',
        eventID
      }
    });
  };

  const resetEventIdAndReload = React.useCallback(() => {
    setEventID(uuid());

    if (reloadOnClear) {
      window.location.reload();
    }
  }, [reloadOnClear, setEventID]);

  const onSuccessClose = useCallback(() => {
    setCheckinSuccess(false);
  }, [setCheckinSuccess]);

  const { mutateAsync, isError, isLoading, error } = useMutation<
    CheckInResponse,
    AxiosError,
    Omit<CheckInParams, 'fp' | 'version'>,
    unknown
  >((params: Parameters<typeof checkInService.checkIn>[0]) => checkInService.checkIn(params), {
    onSuccess: async res => {
      setCheckinSuccess(true);
      resetEventIdAndReload();

      if (res.reloadRequired) {
        setReloadOnClear(true);
      }

      if (res.challenge && res.v) {
        checkInService.challenge({
          locationId: selectedLocation?.locationId ?? '',
          value: decode(res.challenge, res.v)
        });
      }
    }
  });
  // We handle 404 here since it is possible that the
  // record does not exist due to inactivity of user
  // for 28 days. In this case, we want to ask the user
  // to re-register themselves.
  const isForbidden = [401, 403, 404].includes(error?.response?.status ?? 0);

  const onSubmit: SubmitHandler<PrimaryContactWithGuests> = async data => {
    const timestamp = new Date().toISOString();
    return mutateAsync({
      locationId: selectedLocation?.locationId ?? '',
      checkIns: [{ ...data, timestamp, eventID }]
    }).then(() => {
      // Total checkins = Guests + primary
      trackCheckinEvent('checkInSuccessful', { totalCheckins: data.guests.length + 1 });
    });
  };

  useEffect(() => {
    let timer: number;
    if (checkinSuccess) {
      timer = window.setTimeout(() => {
        setCheckinSuccess(false);
      }, constants.CLEAR_CHECKIN_FORM_DELAY);
    }

    return () => {
      window.clearTimeout(timer);
    };
  }, [checkinSuccess]);

  useEffect(() => {
    if (isForbidden) {
      removeAllRegisteredLocations();

      history.push(Routes.Register, {
        isForbiddenError: true
      });
    }
  }, [isForbidden, removeAllRegisteredLocations, history]);

  useEffect(() => {
    if (isIdle) {
      checkinForm.current?.resetForm();
      resetEventIdAndReload();
      trackCheckinEvent('resetInactiveForm');
    }
    // eslint-disable-next-line
  }, [isIdle, resetEventIdAndReload]);

  if (!selectedLocation) {
    return <Redirect to={Routes.Register} />;
  }

  return (
    <Page
      noContentPadding
      banner={{
        title: selectedLocation.businessName,
        content: (
          <BusinessDetailsSubContent
            locationName={selectedLocation.locationName}
            areaName={selectedLocation.areaName}
          />
        )
      }}>
      <CheckinForm
        ref={checkinForm}
        isLoading={isLoading}
        onSubmit={onSubmit}
        onReset={resetEventIdAndReload}
        showGenericError={!isForbidden && isError}
        showSuccess={checkinSuccess}
        onSuccessClose={onSuccessClose}
        trackCheckinEvent={trackCheckinEvent}
      />
      <ContentContainer>
        <Body data-testid='PrivacyText'>
          We’ll only use these details for contact tracing. We’ll delete in 28 days unless needed
          for contact tracing purposes.{' '}
          <LinkButton
            primary
            onClick={e => {
              e.preventDefault();
              modal.open(privacyModal.getDetails, {
                loader: (
                  <CenterAlign>
                    <Spinner />
                  </CenterAlign>
                )
              });
            }}
            data-testid='TogglePrivacyModal'>
            Learn more
          </LinkButton>
          .
        </Body>
      </ContentContainer>
    </Page>
  );
};

export default CheckIn;
